import { classifyInstagramBusinessUrl } from '@mouja/domain/merchant-intake';

import {
  inspectContextValue,
  type ContextSource,
} from '../../lib/classifyContextUrl.js';
import {
  MERCHANT_INTAKE_STORAGE_KEY,
  type MerchantProfileView,
} from '../../merchant/api.js';

/** Same-tab convenience only; no cached profile, account data, or auth authority. */
export const INSTAGRAM_INTAKE_RESUME_KEY = 'mouja:instagram-intake-resume:v1';
export const INSTAGRAM_INTAKE_DRAFT_KEY = 'mouja:instagram-intake-draft:v1';
const DRAFT_TTL_MS = 24 * 60 * 60 * 1_000;

export function clearInstagramIntakeDraft(): void {
  try {
    sessionStorage.removeItem(INSTAGRAM_INTAKE_DRAFT_KEY);
  } catch {
    // Optional same-tab input recovery is not an intake credential.
  }
}

/** Save input before create: a lost response must not imply processing started. */
export function rememberInstagramIntakeDraft(
  sources: readonly ContextSource[],
  contextNote: string,
): void {
  clearInstagramIntakeDraft();
  const source = sources[0];
  if (
    sources.length !== 1 ||
    source?.platform !== 'instagram' ||
    contextNote.length > 2_000
  )
    return;
  const originalUrl = source.originalUrl ?? source.url;
  if (originalUrl.length > 4_096 || classifyInstagramBusinessUrl(originalUrl) === null)
    return;
  try {
    sessionStorage.setItem(
      INSTAGRAM_INTAKE_DRAFT_KEY,
      JSON.stringify({
        originalUrl,
        contextNote,
        expiresAt: new Date(Date.now() + DRAFT_TTL_MS).toISOString(),
      }),
    );
  } catch {
    // Keep the existing submission working when convenience storage is blocked.
  }
}

export function readInstagramIntakeDraft(): {
  readonly source: ContextSource;
  readonly contextNote: string;
} | null {
  try {
    const raw = sessionStorage.getItem(INSTAGRAM_INTAKE_DRAFT_KEY);
    const parsed: unknown =
      raw !== null && raw.length <= 12_288 ? JSON.parse(raw) : null;
    if (parsed !== null && typeof parsed === 'object') {
      const record = parsed as Record<string, unknown>;
      const expiry =
        typeof record['expiresAt'] === 'string' ? Date.parse(record['expiresAt']) : NaN;
      if (
        typeof record['originalUrl'] === 'string' &&
        record['originalUrl'].length <= 4_096 &&
        typeof record['contextNote'] === 'string' &&
        record['contextNote'].length <= 2_000 &&
        expiry > Date.now() &&
        expiry <= Date.now() + DRAFT_TTL_MS
      ) {
        const inspected = inspectContextValue(record['originalUrl'], {
          instagramBusinessEnabled: true,
        });
        if (inspected.kind === 'source' && inspected.source.platform === 'instagram')
          return { source: inspected.source, contextNote: record['contextNote'] };
      }
    }
  } catch {
    // Discard malformed input, never interpret it as an existing intake.
  }
  clearInstagramIntakeDraft();
  return null;
}

export interface InstagramIntakeResume {
  readonly intakeId: string;
  readonly originalUrl: string;
  readonly expiresAt: string;
}

export function clearInstagramIntakeResume(): void {
  try {
    sessionStorage.removeItem(INSTAGRAM_INTAKE_RESUME_KEY);
  } catch {
    // Optional convenience storage never blocks the existing intake flow.
  }
}

function validResume(value: unknown): value is InstagramIntakeResume {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record['intakeId'] === 'string' &&
    /^mouja_int_[A-Za-z0-9_-]{43}$/u.test(record['intakeId']) &&
    typeof record['originalUrl'] === 'string' &&
    record['originalUrl'].length <= 4_096 &&
    classifyInstagramBusinessUrl(record['originalUrl']) !== null &&
    typeof record['expiresAt'] === 'string' &&
    Date.parse(record['expiresAt']) > Date.now()
  );
}

export function readInstagramIntakeResume(): InstagramIntakeResume | null {
  try {
    const raw = sessionStorage.getItem(INSTAGRAM_INTAKE_RESUME_KEY);
    if (raw === null) return null;
    const parsed: unknown = raw.length <= 8_192 ? JSON.parse(raw) : null;
    if (
      validResume(parsed) &&
      parsed.intakeId === localStorage.getItem(MERCHANT_INTAKE_STORAGE_KEY)
    ) {
      return parsed;
    }
  } catch {
    // Corrupt or unavailable browser storage is not a reason to read an intake.
  }
  clearInstagramIntakeResume();
  return null;
}

export function rememberInstagramIntakeResume(
  created: { readonly id: string; readonly expiresAt: string },
  sources: readonly ContextSource[],
): void {
  clearInstagramIntakeResume();
  const source = sources[0];
  if (sources.length !== 1 || source?.platform !== 'instagram') return;
  const record: InstagramIntakeResume = {
    intakeId: created.id,
    originalUrl: source.originalUrl ?? source.url,
    expiresAt: created.expiresAt,
  };
  if (!validResume(record)) return;
  try {
    sessionStorage.setItem(INSTAGRAM_INTAKE_RESUME_KEY, JSON.stringify(record));
    clearInstagramIntakeDraft();
  } catch {
    // The existing localStorage intake ID still supports /business-profile.
  }
}

/** Validate the server view before restoring any presentation from this marker. */
export function resumedInstagramSource(
  resume: InstagramIntakeResume,
  view: MerchantProfileView,
  options: { readonly allowClaimed?: boolean } = {},
): ContextSource | null {
  if (
    view.id !== resume.intakeId ||
    (view.claimed && !options.allowClaimed) ||
    view.status === 'EXPIRED' ||
    !(Date.parse(view.expiresAt) > Date.now()) ||
    view.business.sources.length !== 1
  )
    return null;
  const saved = view.business.sources[0];
  if (saved?.platform !== 'INSTAGRAM') return null;
  const expected = classifyInstagramBusinessUrl(resume.originalUrl);
  const actual = classifyInstagramBusinessUrl(saved.canonicalUrl);
  if (expected === null || actual === null || expected.kind !== actual.kind)
    return null;
  const sameIdentity =
    expected.kind === 'PROFILE'
      ? expected.handle === actual.handle
      : expected.kind === 'SHARE'
        ? expected.canonicalUrl === actual.canonicalUrl
        : expected.shortcode === actual.shortcode;
  if (!sameIdentity) return null;
  const inspected = inspectContextValue(resume.originalUrl, {
    instagramBusinessEnabled: true,
  });
  return inspected.kind === 'source' ? inspected.source : null;
}
